import { APP_FEATURE, useAppFeatures } from '@/lib/app-features';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLinkIcon, PackageIcon, PencilIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import {
  BackLink,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  FactList,
  PageEyebrow,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  carrierLabel,
  piecesLabel,
  deleteShipment,
  readMyIdentity,
  readShipment,
  syncShipmentTracking,
  type Shipment,
  type ShipmentAddress,
  type TrackingEvent,
} from '@/lib/events';
import { cnSpin } from '@/lib/utils';
import { SHIPMENT_TONES, ShipmentDialog } from './event-tabs';

/** One line for a stored address, whichever legacy/Shippo fields it carries. */
function addressLine(address: ShipmentAddress | undefined): string | undefined {
  if (!address) return undefined;
  const line = [
    address.name,
    address.company,
    address.street ?? address.street1,
    [address.city, address.state].filter(Boolean).join(', '),
    address.zip,
  ]
    .filter(Boolean)
    .join(' · ');
  return line || undefined;
}

/** The linked event's id, whichever shape `relatedTo` came back as. */
function relatedEventId(shipment: Shipment): string | undefined {
  const related = shipment.relatedTo;
  if (typeof related === 'string') return related;
  if (typeof related === 'object' && related !== null) return related._id;
  return undefined;
}

/** The linked event's display name — contract · company, like everywhere else. */
function relatedEventName(shipment: Shipment): string | undefined {
  const related = shipment.relatedTo;
  if (typeof related !== 'object' || related === null) return undefined;
  return (
    [related.contractNumber, related.account?.name].filter(Boolean).join(' · ') ||
    related.programDisplayName ||
    related.programName ||
    undefined
  );
}

/** "City, ST" (or whatever parts exist) for a tracking scan's location. */
function scanLocation(scan: TrackingEvent): string | undefined {
  const line = [scan.location?.city, scan.location?.state].filter(Boolean).join(', ');
  return line || undefined;
}

/** The page's two lists search independently, each under its own URL key. */
const SHIPMENT_SEARCH_KEYS = { items: 'itemsQ', scans: 'scansQ' } as const;

/** A contents line with its position (lines have no id) and a searchable name. */
interface ItemRow {
  readonly index: number;
  readonly name: string;
  readonly quantity?: string | number | undefined;
  readonly packingLineId?: string | undefined;
}

/** A carrier scan with its status, place and update as searchable text. */
interface ScanRow {
  readonly index: number;
  readonly scan: TrackingEvent;
  readonly statusLabel: string;
  readonly place: string;
  readonly details: string;
}

/** The fields each search box matches. */
const ITEM_SEARCH_FIELDS: ReadonlyArray<keyof ItemRow> = ['name'];
const SCAN_SEARCH_FIELDS: ReadonlyArray<keyof ScanRow> = ['statusLabel', 'place', 'details'];

/**
 * One shipment in full — tracking state, addresses, contents and, for BCE
 * staff, the same edit/remove controls as the event's shipments tab.
 *
 * @returns The shipment page.
 */
export function ShipmentDetailPage(): React.ReactElement {
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const isFeatureOn = useAppFeatures();
  const materialsOn = isFeatureOn(APP_FEATURE.materials);
  const returnLabelsOn = isFeatureOn(APP_FEATURE.returnLabels);
  const itemSearch = useUrlSearch(SHIPMENT_SEARCH_KEYS.items);
  const scanSearch = useUrlSearch(SHIPMENT_SEARCH_KEYS.scans);

  const me = useQuery({ queryKey: ['staff-me'], queryFn: ({ signal }) => readMyIdentity(signal) });
  const canManage = ['internal', 'admin', 'superadmin'].includes(me.data?.role ?? '');

  const shipment = useQuery({
    queryKey: ['shipment', shipmentId],
    queryFn: ({ signal }) => readShipment(shipmentId ?? '', signal),
    enabled: shipmentId !== undefined,
  });

  const remove = useMutation({
    mutationFn: () => deleteShipment(shipmentId ?? ''),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shipments'] });
      navigate('/shipments');
    },
  });

  const sync = useMutation({
    mutationFn: () => syncShipmentTracking(shipmentId ?? ''),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId] });
      await queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });

  if (shipment.isPending) {
    return (
      <section className="animate-portal-rise">
        <SkeletonTable rows={4} columns={['w-[30%]', 'w-[40%]']} label="Loading shipment…" />
      </section>
    );
  }
  if (shipment.isError) {
    return <ErrorNotice title="Shipment unavailable" message={errorMessage(shipment.error)} />;
  }

  const data = shipment.data;
  const status = data.trackingStatus?.status ?? data.status ?? 'UNKNOWN';
  const eventId = relatedEventId(data);
  // Carrier scans, newest first for reading top-down.
  const history = [...(data.trackingHistory ?? [])].sort(
    (a, b) =>
      (b.statusDate ? Date.parse(b.statusDate) : 0) - (a.statusDate ? Date.parse(a.statusDate) : 0),
  );
  const itemRows = filterRows<ItemRow>(
    (data.items ?? []).map((item, index) => ({ ...item, index, name: item.name ?? '' })),
    itemSearch.search,
    ITEM_SEARCH_FIELDS,
  );
  const scanRows = filterRows<ScanRow>(
    history.map((scan, index) => ({
      index,
      scan,
      statusLabel: (scan.status ?? 'UPDATE').replace(/_/g, ' '),
      place: scanLocation(scan) ?? '',
      details: scan.statusDetails ?? '',
    })),
    scanSearch.search,
    SCAN_SEARCH_FIELDS,
  );
  const trackingUrl =
    data.link && /^https?:\/\//i.test(data.link) ? data.link : undefined;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/shipments">All shipments</BackLink>

      <div className="mt-3 mb-[27px] flex flex-wrap items-end justify-between gap-4">
        <div data-guide="shipment-heading" className="min-w-0">
          <PageEyebrow>OPERATIONS</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1px]">
            {data.carrier ? carrierLabel(data.carrier) : 'Shipment'}{' '}
            <span className="font-mono text-[0.6em] font-normal text-portal-muted">
              {data.trackingNumber}
            </span>
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Pill tone={SHIPMENT_TONES[status] ?? 'neutral'}>{status.replace(/_/g, ' ')}</Pill>
            {returnLabelsOn && data.returnLabel ? <Pill tone="neutral">Return label</Pill> : null}
            {returnLabelsOn && data.returnLabelUnused ? <Pill tone="danger">Unused</Pill> : null}
            {data.vendor ? (
              <span className="text-portal-small text-portal-muted">From {data.vendor}</span>
            ) : null}
          </div>
        </div>
        <div data-guide="shipment-actions" className="flex flex-wrap gap-2 whitespace-nowrap">
          {trackingUrl ? (
            <Button variant="secondary" href={trackingUrl} target="_blank" rel="noreferrer">
              <ExternalLinkIcon aria-hidden className="size-4" /> Track with carrier
            </Button>
          ) : null}
          {canManage ? (
            <>
              <Button
                variant="secondary"
                onClick={() => sync.mutate()}
                disabled={sync.isPending || !data.trackingNumber}
              >
                <RefreshCwIcon aria-hidden className={cnSpin(sync.isPending)} />
                {sync.isPending ? 'Syncing…' : 'Sync'}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                <PencilIcon aria-hidden className="size-4" /> Edit
              </Button>
              <Button variant="secondary" onClick={() => setRemoving(true)}>
                <XIcon aria-hidden className="size-4" /> Remove
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {sync.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Sync failed" message={errorMessage(sync.error)} />
        </div>
      ) : null}
      {sync.isSuccess && !sync.data.synced ? (
        <div className="mt-3">
          <ErrorNotice
            inline
            title="Nothing to sync"
            message={sync.data.reason ?? 'The carrier had no update for this shipment.'}
          />
        </div>
      ) : null}

      <Panel guide="shipment-facts" className="p-5">
        <FactList
          columns={2}
          showEmpty
          facts={[
            {
              label: 'Event',
              value: eventId ? (
                <Link
                  to={`/events/${eventId}?tab=shipments`}
                  className="font-bold text-brand hover:underline"
                >
                  {relatedEventName(data) ?? 'View event'}
                </Link>
              ) : undefined,
            },
            {
              label: 'Latest tracking update',
              value: data.trackingStatus?.statusDetails,
            },
            { label: 'ETA', value: data.eta ? new Date(data.eta).toLocaleDateString() : undefined },
            { label: 'PRO number', value: data.proNumber },
            { label: 'Pieces', value: piecesLabel(data) },
            {
              label: 'Expected arrival',
              value: data.expectedArrivalDate
                ? new Date(data.expectedArrivalDate).toLocaleDateString()
                : undefined,
            },
            {
              label: 'Box label',
              // Line breaks are the venue's: each one is a line on the label.
              value: data.labelText ? <span className="whitespace-pre-line">{data.labelText}</span> : undefined,
            },
            { label: 'From', value: addressLine(data.addressFrom) },
            { label: 'To', value: addressLine(data.addressTo) },
            {
              label: 'Viewable by client',
              // A return label never reaches the client's portal, whatever the switch says.
              value: data.returnLabel ? 'No, it is a return label' : data.viewableByClient === undefined ? undefined : data.viewableByClient ? 'Yes' : 'No',
            },
          ]}
        />
      </Panel>

      <div className="mt-6">
        <h2 data-guide="shipment-contents" className="mb-3 text-portal-section font-bold">Contents</h2>
        {(data.items ?? []).length > 0 ? (
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search contents"
              placeholder="Item name"
              value={itemSearch.search}
              onChange={(event) => itemSearch.setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>
        ) : null}
        {(data.items ?? []).length === 0 ? (
          <Panel>
            <EmptyState icon={<PackageIcon aria-hidden className="size-6" />} title="No items recorded" message="Edit the shipment to list what is in it, typed by hand or picked from the event's materials." />
          </Panel>
        ) : itemRows.length === 0 ? (
          <Panel>
            <EmptyState title="No items match" message="Try a different search." />
          </Panel>
        ) : (
          <DataTable
            headers={['Item', 'Quantity']}
            rightAlignedHeaders={[1]}
            minWidth={480}
          >
            {itemRows.map((item) => (
              <tr
                key={`${item.name}-${item.index}`}
                className="border-t border-portal-line align-top"
              >
                <td className="p-3 text-portal-small">
                  <span className="block">{item.name || '—'}</span>
                  {item.packingLineId && eventId && materialsOn ? (
                    <Link to={`/events/${eventId}?tab=materials`} className="text-portal-micro text-brand hover:underline">
                      From materials
                    </Link>
                  ) : null}
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">
                  {item.quantity ?? '—'}
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>

      {history.length > 0 ? (
        <div className="mt-6">
          <h2 data-guide="shipment-history" className="mb-3 text-portal-section font-bold">Tracking history</h2>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search tracking history"
              placeholder="Status, location or update"
              value={scanSearch.search}
              onChange={(event) => scanSearch.setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>
          {scanRows.length === 0 ? (
            <Panel>
              <EmptyState title="No scans match" message="Try a different search." />
            </Panel>
          ) : (
            <DataTable headers={['Status', 'Date', 'Location', 'Update']} minWidth={720}>
              {scanRows.map(({ scan, index, statusLabel, place, details }) => (
                <tr
                  key={`${scan.statusDate ?? 'scan'}-${index}`}
                  className="border-t border-portal-line align-top"
                >
                  <td className="p-3">
                    <Pill tone={SHIPMENT_TONES[scan.status ?? ''] ?? 'neutral'}>
                      {statusLabel}
                    </Pill>
                  </td>
                  <td className="p-3 text-portal-small whitespace-nowrap">
                    {scan.statusDate
                      ? new Date(scan.statusDate).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : '—'}
                  </td>
                  <td className="p-3 text-portal-small">{place || '—'}</td>
                  <td className="p-3 text-portal-small text-portal-muted">
                    {details || '—'}
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
        </div>
      ) : null}

      {/* Keyed by id so a refetched shipment reseeds the form. */}
      {editing && shipmentId ? (
        <ShipmentDialog
          key={shipmentId}
          eventId={eventId ?? ''}
          existing={data}
          open
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId] });
            await queryClient.invalidateQueries({ queryKey: ['shipments'] });
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removing}
        title="Remove this shipment?"
        message={`${[data.carrier, data.trackingNumber].filter(Boolean).join(' ') || 'This shipment'} will be removed. This cannot be undone.`}
        confirmLabel="Remove"
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setRemoving(false)}
      />
    </section>
  );
}
